import { preprocessLedgerText } from './ledgerTextPreprocessing'
import { stripFoliationMarkers } from './manualStudioSegmentationResult'
import { HIDDEN_EDITORIAL_MARKER_REGEX_SOURCE, shouldHideMarkerInWorkspace } from './workspaceMarkerVisibility'

export interface SegmentableBoundaryInfo {
  startIndex: number
  endIndex: number
}

export interface LedgerSafeUnitSplitWord {
  text: string
  segmentableWordIndex: number
  splitBoundaryWordIndex: number | null
}

export interface LedgerSafeCanonicalSplitDisplayWords {
  words: LedgerSafeUnitSplitWord[]
  minSplitBoundaryWordIndex: number
  maxSplitBoundaryWordIndex: number
  sourceStartWordIndex: number
  sourceEndWordIndex: number
}

export interface LedgerSafeSegmentableSourceWordEntry {
  sourceWordIndex: number
  normalized: string
}

export const normalizeSplitComparableWord = (value: string): string => value
  .normalize('NFKC')
  .replace(/^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu, '')
  .replace(/[\u064B-\u065F\u0670]/g, '')
  .trim()

const DIGITS_ONLY = /^[0-9\u0660-\u0669\u06F0-\u06F9]+$/
const BRACKETED_DIGITS_ONLY = /^\[\s*[0-9\u0660-\u0669\u06F0-\u06F9]+\s*\]$/
const ARABIC_TRAILING_FOOTNOTE_ANCHOR = /([\u0600-\u06FF])[*‡§]?[0-9\u0660-\u0669\u06F0-\u06F9]+(?=$|[.,;:،؛])/g

const isAtLogicalLineStart = (sourceText: string, start: number): boolean => {
  for (let index = start - 1; index >= 0; index -= 1) {
    const char = sourceText[index]
    if (char === '\n' || char === '\r') {
      return true
    }
    if (char !== ' ' && char !== '\t') {
      return false
    }
  }
  return true
}

const hasTextAfterInlineNumberPrefix = (sourceText: string, end: number): boolean => {
  let index = end
  while (index < sourceText.length && (sourceText[index] === ' ' || sourceText[index] === '\t')) {
    index += 1
  }
  return index < sourceText.length && sourceText[index] !== '\n' && sourceText[index] !== '\r'
}

const isStandaloneNumericLineWord = (sourceText: string, end: number): boolean => {
  let index = end
  while (index < sourceText.length && (sourceText[index] === ' ' || sourceText[index] === '\t')) {
    index += 1
  }
  return index >= sourceText.length || sourceText[index] === '\n' || sourceText[index] === '\r'
}

export const normalizeSplitComparableSourceWord = (
  sourceText: string,
  span: { start: number; end: number }
): string => {
  const rawWord = stripFoliationMarkers(sourceText.slice(span.start, span.end))
  if (BRACKETED_DIGITS_ONLY.test(rawWord.trim())) {
    return ''
  }
  const comparableWord = normalizeSplitComparableWord(rawWord.replace(ARABIC_TRAILING_FOOTNOTE_ANCHOR, '$1'))
  if (
    DIGITS_ONLY.test(comparableWord) &&
    isAtLogicalLineStart(sourceText, span.start) &&
    (isStandaloneNumericLineWord(sourceText, span.end) || hasTextAfterInlineNumberPrefix(sourceText, span.end))
  ) {
    return ''
  }
  return comparableWord
}

export const findContiguousWordOffset = (sourceWords: string[], canonicalWords: string[]): number | null => {
  if (canonicalWords.length === 0) {
    return 0
  }
  if (sourceWords.length < canonicalWords.length) {
    return null
  }
  for (let start = 0; start <= sourceWords.length - canonicalWords.length; start += 1) {
    let matches = true
    for (let index = 0; index < canonicalWords.length; index += 1) {
      if (sourceWords[start + index] !== canonicalWords[index]) {
        matches = false
        break
      }
    }
    if (matches) {
      return start
    }
  }
  return null
}

export const findNearestContiguousWordOffset = (
  sourceWords: string[],
  canonicalWords: string[],
  preferredOffset: number
): number | null => {
  if (canonicalWords.length === 0) {
    return 0
  }
  if (sourceWords.length < canonicalWords.length) {
    return null
  }

  let bestOffset: number | null = null
  let bestDistance = Number.MAX_SAFE_INTEGER
  for (let start = 0; start <= sourceWords.length - canonicalWords.length; start += 1) {
    let matches = true
    for (let index = 0; index < canonicalWords.length; index += 1) {
      if (sourceWords[start + index] !== canonicalWords[index]) {
        matches = false
        break
      }
    }
    if (!matches) {
      continue
    }
    const distance = Math.abs(start - preferredOffset)
    if (distance < bestDistance) {
      bestOffset = start
      bestDistance = distance
    }
  }
  return bestOffset
}

export const normalizeSegmentableSourceWordEntries = (
  sourceText: string,
  segmentableWordSpans: Array<{ start: number; end: number }>,
  startIndex: number,
  endIndex: number
): LedgerSafeSegmentableSourceWordEntry[] =>
  segmentableWordSpans
    .slice(startIndex, endIndex + 1)
    .map((span, offset) => ({
      sourceWordIndex: startIndex + offset,
      normalized: normalizeSplitComparableSourceWord(sourceText, span)
    }))
    .filter(entry => entry.normalized.length > 0)

export const buildCanonicalSplitDisplayWords = ({
  unitText,
  sourceText,
  wordInfo,
  segmentableWordSpans
}: {
  unitText: string
  sourceText: string
  wordInfo: SegmentableBoundaryInfo
  segmentableWordSpans: Array<{ start: number; end: number }>
}): LedgerSafeCanonicalSplitDisplayWords | null => {
  const buildFromDisplayText = (displayText: string): LedgerSafeCanonicalSplitDisplayWords | null => {
    // Recognize complete markers before whitespace splitting: their attributes
    // remain visible but must not be matched against segmentable source words.
    const displayTokens = Array.from(displayText.matchAll(
      new RegExp(`${HIDDEN_EDITORIAL_MARKER_REGEX_SOURCE}|\\S+`, 'gi')
    )).flatMap(([part]) => part.split(/\s+/).filter(Boolean).map(text => ({
      text,
      marker: shouldHideMarkerInWorkspace(part)
    })))
    const displayTokenPayloads = displayTokens.map(({ text: token, marker }) => {
      const normalized = normalizeSplitComparableWord(token)
      return {
        text: token,
        isSegmentable: !marker && token.trim().length > 0 &&
          !BRACKETED_DIGITS_ONLY.test(token.trim()) &&
          !shouldHideMarkerInWorkspace(token) &&
          normalized.length > 0,
        normalized
      }
    })

    const canonicalSegmentableWords = displayTokenPayloads
      .filter(token => token.isSegmentable)
      .map(token => token.normalized)

    if (canonicalSegmentableWords.length < 2) {
      return null
    }

    const sourceWordEntries = normalizeSegmentableSourceWordEntries(
      sourceText,
      segmentableWordSpans,
      wordInfo.startIndex,
      wordInfo.endIndex
    )
    const sourceWords = sourceWordEntries.map(entry => entry.normalized)

    const sourceOffset = findContiguousWordOffset(sourceWords, canonicalSegmentableWords)
    let matchedSourceWordIndexes: number[] | null = sourceOffset === null
      ? null
      : sourceWordEntries
        .slice(sourceOffset, sourceOffset + canonicalSegmentableWords.length)
        .map(entry => entry.sourceWordIndex)

    if (!matchedSourceWordIndexes || matchedSourceWordIndexes.length !== canonicalSegmentableWords.length) {
      const expansion = Math.min(80, Math.max(8, Math.ceil(canonicalSegmentableWords.length * 0.08)))
      const windowStartIndex = Math.max(0, wordInfo.startIndex - expansion)
      const windowEndIndex = Math.min(segmentableWordSpans.length - 1, wordInfo.endIndex + expansion)
      const windowSourceWordEntries = normalizeSegmentableSourceWordEntries(
        sourceText,
        segmentableWordSpans,
        windowStartIndex,
        windowEndIndex
      )
      const windowSourceWords = windowSourceWordEntries.map(entry => entry.normalized)
      const windowOffset = findNearestContiguousWordOffset(
        windowSourceWords,
        canonicalSegmentableWords,
        wordInfo.startIndex - windowStartIndex
      )
      if (windowOffset === null) {
        return null
      }
      matchedSourceWordIndexes = windowSourceWordEntries
        .slice(windowOffset, windowOffset + canonicalSegmentableWords.length)
        .map(entry => entry.sourceWordIndex)
    }

    if (!matchedSourceWordIndexes || matchedSourceWordIndexes.length !== canonicalSegmentableWords.length) {
      return null
    }
    const resolvedMatchedSourceWordIndexes = matchedSourceWordIndexes
    const sourceStartIndex = resolvedMatchedSourceWordIndexes[0] ?? null
    const sourceEndIndex = resolvedMatchedSourceWordIndexes[resolvedMatchedSourceWordIndexes.length - 1] ?? null
    if (sourceStartIndex === null || sourceEndIndex === null || sourceEndIndex >= segmentableWordSpans.length) {
      return null
    }

    const resolvedSourceStartIndex = sourceStartIndex
    const resolvedSourceEndIndex = sourceEndIndex
    let canonicalSegmentableIndex = 0
    const words = displayTokenPayloads.map(({ text, isSegmentable }): LedgerSafeUnitSplitWord => {
      if (!isSegmentable) {
        return {
          text,
          segmentableWordIndex: -1,
          splitBoundaryWordIndex: null
        }
      }
      const sourceWordIndex = resolvedMatchedSourceWordIndexes[canonicalSegmentableIndex] ?? resolvedSourceStartIndex + canonicalSegmentableIndex
      const splitBoundaryWordIndex = canonicalSegmentableIndex < canonicalSegmentableWords.length - 1
        ? resolvedMatchedSourceWordIndexes[canonicalSegmentableIndex + 1] ?? sourceWordIndex + 1
        : null
      canonicalSegmentableIndex += 1
      return {
        text,
        segmentableWordIndex: sourceWordIndex,
        splitBoundaryWordIndex
      }
    })

    return {
      words,
      minSplitBoundaryWordIndex: resolvedMatchedSourceWordIndexes[1] ?? resolvedSourceStartIndex + 1,
      maxSplitBoundaryWordIndex: resolvedSourceEndIndex,
      sourceStartWordIndex: resolvedSourceStartIndex,
      sourceEndWordIndex: resolvedSourceEndIndex
    }
  }

  const workspaceDisplayText = preprocessLedgerText(unitText).workspaceDisplayText
  const workspaceResult = buildFromDisplayText(workspaceDisplayText)
  if (workspaceResult) {
    return workspaceResult
  }
  const rawDisplayText = String(unitText || '').replace(/\s+/g, ' ').trim()
  return rawDisplayText && rawDisplayText !== workspaceDisplayText
    ? buildFromDisplayText(rawDisplayText)
    : null
}
